import { useState, useEffect } from "react";
import api from "../lib/api";
import { Star, Send, TrendingUp, MessageCircle, ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { toast } from "sonner";

const STAR_COLORS = ["#8A2A23", "#805B10", "#B5A493", "#2D5936", "#1A1A1A"];

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRating, setFilterRating] = useState("all");

  useEffect(() => {
    const load = async () => {
      try {
        const [revRes, statsRes, cliRes] = await Promise.all([
          api.get("/reviews"), api.get("/reviews/stats"), api.get("/clients?limit=200")
        ]);
        setReviews(revRes.data); setStats(statsRes.data); setClients(cliRes.data.clients || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const requestReview = async (clientId) => {
    try {
      await api.post(`/reviews/request?client_id=${clientId}`);
      toast.success("Review request sent!");
    } catch (err) { toast.error("Failed to send"); }
  };

  const filtered = filterRating === "all" ? reviews : reviews.filter(r => r.rating === Number(filterRating));
  const distData = stats?.distribution ? Object.entries(stats.distribution).map(([k, v]) => ({ rating: `${k} star`, count: v, fill: STAR_COLORS[Number(k) - 1] })) : [];

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="reviews-page">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Reviews & Reputation</h1>
          <p className="text-slate-600 text-sm mt-1">Client feedback, ratings, and NPS</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><Star className="w-5 h-5 text-rose-400" fill="#B5A493" strokeWidth={0} /></div>
          <p className="font-['Outfit'] text-4xl font-medium text-slate-900">{stats?.avg_rating || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Average Rating</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><MessageCircle className="w-5 h-5 text-slate-400" strokeWidth={1.5} /></div>
          <p className="font-['Outfit'] text-4xl font-medium text-slate-900">{stats?.total || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Total Reviews</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5 text-slate-400" strokeWidth={1.5} /></div>
          <p className={`font-['Outfit'] text-4xl font-medium ${(stats?.nps || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{stats?.nps || 0}</p>
          <p className="text-sm text-slate-400 mt-1">NPS Score</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><ThumbsUp className="w-5 h-5 text-emerald-600" strokeWidth={1.5} /></div>
          <p className="font-['Outfit'] text-4xl font-medium text-emerald-600">{stats?.distribution?.[5] || 0}</p>
          <p className="text-sm text-slate-400 mt-1">5-Star Reviews</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Distribution */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="rating-distribution">
          <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-4">Rating Distribution</h3>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E4E0" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#8A8A8A', fontSize: 11 }} />
                <YAxis type="category" dataKey="rating" axisLine={false} tickLine={false} tick={{ fill: '#1A1A1A', fontSize: 12 }} width={60} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E6E4E0' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {distData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Provider Ratings */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="provider-ratings">
          <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-4">Provider Ratings</h3>
          <div className="space-y-3">
            {(stats?.by_provider || []).map(p => (
              <div key={p.provider_id} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                <span className="text-sm font-medium text-slate-900">{p.name}</span>
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className="w-3.5 h-3.5" fill={s <= Math.round(p.avg_rating) ? "#B5A493" : "none"} stroke={s <= Math.round(p.avg_rating) ? "#B5A493" : "#E6E4E0"} strokeWidth={1.5} />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-slate-900">{p.avg_rating}</span>
                  <span className="text-xs text-slate-400">({p.count})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900">Recent Reviews</h3>
          <Select value={filterRating} onValueChange={setFilterRating}>
            <SelectTrigger data-testid="review-filter" className="w-[140px] h-8 border-slate-200 rounded-lg text-sm"><SelectValue placeholder="All ratings" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ratings</SelectItem>
              {[5,4,3,2,1].map(r => <SelectItem key={r} value={String(r)}>{r} Stars</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="divide-y divide-[#E6E4E0]">
          {filtered.map(review => (
            <div key={review.review_id} className="p-5 hover:bg-[#FAFAFA] transition-colors" data-testid={`review-${review.review_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-slate-900">{review.client_name}</span>
                    <span className="text-xs text-slate-400">for {review.provider_name}</span>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className="w-4 h-4" fill={s <= review.rating ? "#B5A493" : "none"} stroke={s <= review.rating ? "#B5A493" : "#E6E4E0"} strokeWidth={1.5} />
                    ))}
                  </div>
                  {review.comment && <p className="text-sm text-slate-600">"{review.comment}"</p>}
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <span className="text-xs text-slate-400">{review.created_at ? new Date(review.created_at).toLocaleDateString() : ""}</span>
                  <p className="text-[10px] text-rose-400 mt-0.5 capitalize">{review.source}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
